import { useEffect, useMemo, useState } from 'react';

import { useSurvey, useSurveyTemplate, useView } from '@/hooks';
import { SurveyResponseStatus } from '@/types';
import { Alert, Button, Divider, Icon, Snackbar, Typography } from '@/ui';
import { isValidEmail, track } from '@/utils';

import type { Recipient } from '@/types';
import type { ChangeEvent, FC } from 'react';

import Email from '@/components/Insights/Survey/SurveyBuilder/Email';
import Recipients from '@/components/Insights/Survey/SurveyBuilder/Recipients';
import { useDeliverSurveyMutation } from '@/store/api/surveyApi';

const ManageRecipients: FC = () => {
  const { isClient } = useView();
  const { survey, isSurveyLoading, refetchSurvey } = useSurvey();
  const { surveyTemplate, isSurveyTemplateLoading } = useSurveyTemplate();

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastDeliveredCount, setLastDeliveredCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deliverSurvey, { isLoading: isDelivering }] =
    useDeliverSurveyMutation();

  useEffect(() => {
    if (!isSurveyLoading && !isSurveyTemplateLoading) {
      const initial = [
        ...(survey.responses ?? []),
        { email: '', name: '', relationship: '' },
      ];
      setRecipients(initial);
      const initialEmail =
        survey.emailBody && survey.emailBody.length > 0
          ? survey.emailBody
          : surveyTemplate.emailTemplate;
      setEmailTemplate(initialEmail);
    }
  }, [
    isSurveyLoading,
    isSurveyTemplateLoading,
    survey.responses,
    survey.emailBody,
    surveyTemplate.emailTemplate,
  ]);

  const respondedBy = useMemo(() => {
    const responses = survey.responses ?? [];
    const aggregatedCount = survey.aggregated?.totalResponses;
    const completed =
      aggregatedCount !== undefined
        ? aggregatedCount
        : responses.filter((r) => r.status === SurveyResponseStatus.COMPLETED)
            .length;
    return completed
      ? `${completed} out of ${responses.length} coworkers responded`
      : 'No responses yet';
  }, [survey.responses, survey.aggregated?.totalResponses]);

  // Note: we only deliver to new recipients; existing are read-only

  const existingCount = useMemo(
    () => (survey.responses ?? []).length,
    [survey.responses],
  );

  const newRecipients = useMemo(
    () =>
      recipients
        .slice(existingCount)
        .filter(
          ({ name, email, relationship }) => name && email && relationship,
        ),
    [recipients, existingCount],
  );

  const isValid = useMemo(
    () =>
      newRecipients.every(({ email }) => !isValidEmail(email ?? '')) &&
      newRecipients.length >= 1,
    [newRecipients],
  );

  const onChangeTemplate = (e: ChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setEmailTemplate(value);
  };

  const onDeliver = async () => {
    track('Clicked deliver survey');
    setErrorMessage(null);

    try {
      setLastDeliveredCount(newRecipients.length);
      await deliverSurvey({
        emailTemplate,
        recipients: newRecipients,
        surveyId: survey.id,
      }).unwrap();
      await refetchSurvey();
      setShowSuccess(true);
    } catch (err: unknown) {
      const responseData = (err as { data?: unknown } | undefined)?.data as
        | { errors?: Record<string, unknown>; error?: string }
        | undefined;

      const errors = responseData?.errors;
      const emailErrors = errors?.email;

      if (Array.isArray(emailErrors) && typeof emailErrors[0] === 'string') {
        setErrorMessage(`Email ${emailErrors[0]}`);
        return;
      }

      if (errors && typeof errors === 'object') {
        const firstError = Object.values(errors)
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .find((value) => typeof value === 'string') as string | undefined;

        if (firstError) {
          setErrorMessage(firstError);
          return;
        }
      }

      if (responseData?.error) {
        setErrorMessage(responseData.error);
        return;
      }

      setErrorMessage('Unable to send to new recipients. Please try again.');
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex flex-col gap-2">
        <Typography variant="h5">Invite coworkers</Typography>
        <Typography className="text-cement-500" variant="body2">
          {respondedBy}
        </Typography>
      </div>

      <Recipients
        disabledUntil={(survey.responses ?? []).length}
        maximum={surveyTemplate.maximumRecipients}
        recipients={recipients}
        setRecipients={isClient ? setRecipients : () => {}}
      />

      <Divider />

      <div className="flex flex-col gap-2 lg:flex-row">
        <div className="flex-1">
          <Typography className="mb-2" variant="subtitle1">
            Review the email your coworkers will receive
          </Typography>
          <Email setTemplate={onChangeTemplate} template={emailTemplate} />
        </div>
        <div className="flex w-full flex-col gap-3 lg:w-80">
          <Button
            color="secondary"
            disabled={!isClient || !isValid || isDelivering}
            endIcon={<Icon>send</Icon>}
            variant="contained"
            onClick={onDeliver}
          >
            Send to new recipients
          </Button>
          {!isValid && (
            <Typography className="text-cement-400" variant="caption">
              Add at least 1 valid recipient to send.
            </Typography>
          )}
        </div>
      </div>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={4000}
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      >
        <Alert
          severity="success"
          style={{ width: '100%' }}
          onClose={() => setShowSuccess(false)}
        >
          Sent to {lastDeliveredCount} recipient
          {lastDeliveredCount !== 1 ? 's' : ''}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={7000}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          severity="error"
          style={{ width: '100%' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManageRecipients;
